/**
 * Single entry point for pages: composes fileProcessor -> uploadQueue -> stateUploader.
 */

import * as fileProcessor from '@renderer/utils/pipeline/fileProcessor'
import { uploadFile } from '@renderer/utils/pipeline/uploadQueue'
import * as stateUploader from '@renderer/utils/pipeline/stateUploader'
import { summarizePhotoUrlsForUploadDebug } from '@renderer/utils/imageCacheUtils'
import type {
  ProcessingContext,
  UploadResult,
  WatermarkConfig,
  GalleryItemForProcessing,
  ProcessedFile,
  MediaType
} from '@renderer/types/outputPipeline.types'
import type { ProcessVideoTaskParams } from '@renderer/utils/pipeline/fileProcessor'
import {
  unregisteredOutcome,
  type UnregisteredOutcome
} from '@renderer/utils/pipeline/uploadOutcome'

type SingleFileUploadType = 'strip' | 'gif' | 'image' | 'video'

/**
 * Why files uploadFile returned are not in the gallery, and how loud to say it.
 *
 * uploadFile always returns `localUrl` — it is built from the blob before any
 * network call — so only `uploadedUrl`, set once the gallery POST succeeds,
 * says the guest can see the file. Without a signed-in user nothing is
 * attempted at all; a refusal from the server is an error; anything else was
 * handed to the retry queue and is a warning (see uploadOutcome.ts).
 */
function notRegistered(unregistered: UploadResult[], ctx: ProcessingContext): UnregisteredOutcome {
  return unregisteredOutcome(unregistered, !!ctx.context?.user?.email)
}

/** Activity-log details for a not-registered line. */
function outcomeDetails(outcome: UnregisteredOutcome): Record<string, unknown> {
  return { level: outcome.level, ...(outcome.code ? { code: outcome.code } : {}) }
}

function fileToProcessed(file: File, uploadType: SingleFileUploadType): ProcessedFile {
  const fileType: MediaType =
    uploadType === 'video' ? 'videos' : uploadType === 'gif' ? 'gifs' : 'photos'
  return {
    file,
    fileName: file.name,
    fileType,
    uploadType,
    metadata: { mimeType: file.type }
  }
}

export async function processAndUploadStrip(
  stripBlob: Blob,
  ctx: ProcessingContext
): Promise<UploadResult | null> {
  try {
    const processed = fileProcessor.processStrip(stripBlob, ctx.sessionId)
    const result = await uploadFile(processed, ctx)
    if (result.uploadedUrl) {
      await stateUploader.logActivity(
        ctx.sessionId,
        `Strip upload completed: ${processed.fileName}`,
        'info',
        { fileName: processed.fileName },
        'outputOrchestrator'
      )
    } else {
      const outcome = notRegistered([result], ctx)
      await stateUploader.logActivity(
        ctx.sessionId,
        `Strip upload ${outcome.reason}: ${processed.fileName}`,
        outcome.level,
        { fileName: processed.fileName, ...outcomeDetails(outcome) },
        'outputOrchestrator'
      )
    }
    return result
  } catch (error) {
    console.error('[outputOrchestrator] processAndUploadStrip failed:', error)
    await stateUploader.logActivity(
      ctx.sessionId,
      `Strip upload failed: ${error instanceof Error ? error.message : String(error)}`,
      'error',
      { error: String(error) },
      'outputOrchestrator'
    )
    return null
  }
}

/**
 * Result of processAndUploadGIF.
 *
 * The *LocalUrl fields are blob: URLs for on-screen preview and say nothing
 * about whether the bytes reached the gallery — read gifUploaded/mp4Uploaded
 * for that.
 */
export interface ProcessAndUploadGIFResult {
  gifLocalUrl: string | null
  mp4LocalUrl?: string | null
  /** True only once the GIF is registered in the gallery. */
  gifUploaded: boolean
  /** True only once the MP4 twin is registered in the gallery. */
  mp4Uploaded: boolean
}

export async function processAndUploadGIF(
  photoUrls: string[],
  gifSpeed: number,
  ctx: ProcessingContext,
  customGifFrame?: string,
  onSuccess?: (gifLocalUrl: string, mp4LocalUrl?: string | null) => void
): Promise<ProcessAndUploadGIFResult> {
  try {
    const { files, errors } = await fileProcessor.processGIF(
      photoUrls,
      gifSpeed,
      ctx.sessionId,
      customGifFrame
    )
    const results = await Promise.all(
      files.map(async (p) => ({ fileType: p.fileType, result: await uploadFile(p, ctx) }))
    )
    // Match on fileType, not position: either encoder can be missing now, so index 0 is not
    // guaranteed to be the GIF.
    const gif = results.find((r) => r.fileType === 'gifs')?.result
    const mp4 = results.find((r) => r.fileType === 'videos')?.result
    const gifLocalUrl = gif?.localUrl ?? null
    const mp4LocalUrl = mp4?.localUrl ?? null

    // uploadFile always returns a localUrl — it is built from the blob before any
    // network call — so success must be read from uploadedUrl, which is set only
    // after the upload is registered in the gallery. Reading localUrl here is what
    // used to log "completed" for a GIF that never left the booth.
    const gifUploaded = !!gif?.uploadedUrl
    const mp4Uploaded = !!mp4?.uploadedUrl

    // The preview is driven off the local blob regardless: the retry queue usually
    // heals the upload, and blanking the screen would be worse.
    if (gifLocalUrl && onSuccess) onSuccess(gifLocalUrl, mp4LocalUrl)

    // Two distinct kinds of problem, both previously invisible: an encoder that
    // never produced a file (errors, from processGIF) and a file that was produced
    // but never registered in the gallery (present locally, no uploadedUrl).
    const problems = [...errors]
    if (gif && !gifUploaded) problems.push('GIF not registered in gallery')
    if (mp4 && !mp4Uploaded) problems.push('MP4 not registered in gallery')

    if (problems.length === 0) {
      await stateUploader.logActivity(
        ctx.sessionId,
        `GIF/MP4 upload completed (${files.length} files)`,
        'info',
        { gifUploaded, mp4Uploaded },
        'outputOrchestrator'
      )
    } else {
      const unregistered = [gif, mp4].filter((r): r is UploadResult => !!r && !r.uploadedUrl)
      const outcome = notRegistered(unregistered, ctx)
      // An encoder that produced nothing is lost for good; so is a refusal. A file that is
      // only waiting on the retry queue is a warning. (Before, this line carried no level
      // and so landed as info.)
      const level = errors.length > 0 ? 'error' : outcome.level
      const refusal = outcome.code ? `: refused by the server (${outcome.code})` : ''
      await stateUploader.logActivity(
        ctx.sessionId,
        `GIF/MP4 upload incomplete: ${problems.join('; ')}${refusal}`,
        level,
        {
          gifUploaded,
          mp4Uploaded,
          errors,
          handedOffToRetryQueue: true,
          ...outcomeDetails(outcome),
          level
        },
        'outputOrchestrator'
      )
    }
    return { gifLocalUrl, mp4LocalUrl, gifUploaded, mp4Uploaded }
  } catch (error) {
    console.error('[outputOrchestrator] processAndUploadGIF failed:', error)
    await stateUploader.logActivity(
      ctx.sessionId,
      `GIF upload failed: ${error instanceof Error ? error.message : String(error)}`,
      'error',
      { error: String(error) },
      'outputOrchestrator'
    )
    return { gifLocalUrl: null, mp4LocalUrl: null, gifUploaded: false, mp4Uploaded: false }
  }
}

export async function processAndUploadPhotos(
  galleryItems: GalleryItemForProcessing[],
  ctx: ProcessingContext,
  watermarkConfig?: WatermarkConfig,
  onError?: (error: Error) => void
): Promise<void> {
  try {
    const processedList = await fileProcessor.processIndividualPhotos(
      galleryItems,
      ctx.sessionId,
      watermarkConfig
    )
    const results = await Promise.all(processedList.map((p) => uploadFile(p, ctx)))
    const registered = results.filter((r) => r.uploadedUrl).length
    if (registered === processedList.length) {
      await stateUploader.logActivity(
        ctx.sessionId,
        `Individual photos upload completed (${processedList.length} photos)`,
        'info',
        {},
        'outputOrchestrator'
      )
    } else {
      const outcome = notRegistered(
        results.filter((r) => !r.uploadedUrl),
        ctx
      )
      await stateUploader.logActivity(
        ctx.sessionId,
        `Individual photos upload incomplete: ${registered} of ${processedList.length} in the gallery, the rest ${outcome.reason}`,
        outcome.level,
        { registered, total: processedList.length, ...outcomeDetails(outcome) },
        'outputOrchestrator'
      )
    }
  } catch (error) {
    console.error('[outputOrchestrator] processAndUploadPhotos failed:', error)
    const err = error instanceof Error ? error : new Error(String(error))
    if (onError) onError(err)
    const debug = summarizePhotoUrlsForUploadDebug(galleryItems)
    console.warn('[outputOrchestrator] Photo upload failure context:', debug)
    await stateUploader.logActivity(
      ctx.sessionId,
      `Photos upload failed: ${err.message}`,
      'error',
      { error: err.message, ...debug },
      'outputOrchestrator'
    )
  }
}

export function processAndUploadVideo(
  params: ProcessVideoTaskParams
): string | null {
  try {
    return fileProcessor.processVideoTask(params)
  } catch (error) {
    console.error('[outputOrchestrator] processAndUploadVideo failed:', error)
    if (params.onError) {
      params.onError(error instanceof Error ? error : new Error(String(error)))
    }
    return null
  }
}

/**
 * Upload a single pre-built File (e.g. recording blob, strip from edit flow).
 * Use for Record.tsx, edit/Select, and Filter pages that upload one file at a time.
 */
export async function processAndUploadSingleFile(
  file: File,
  uploadType: SingleFileUploadType,
  ctx: ProcessingContext
): Promise<UploadResult> {
  try {
    const processed = fileToProcessed(file, uploadType)
    const result = await uploadFile(processed, ctx)
    if (result.uploadedUrl) {
      await stateUploader.logActivity(
        ctx.sessionId,
        `${uploadType} upload completed: ${processed.fileName}`,
        'info',
        { fileName: processed.fileName },
        'outputOrchestrator'
      )
    } else {
      const outcome = notRegistered([result], ctx)
      await stateUploader.logActivity(
        ctx.sessionId,
        `${uploadType} upload ${outcome.reason}: ${processed.fileName}`,
        outcome.level,
        { fileName: processed.fileName, ...outcomeDetails(outcome) },
        'outputOrchestrator'
      )
    }
    return result
  } catch (error) {
    console.error('[outputOrchestrator] processAndUploadSingleFile failed:', error)
    await stateUploader.logActivity(
      ctx.sessionId,
      `${uploadType} upload failed: ${error instanceof Error ? error.message : String(error)}`,
      'error',
      { error: String(error) },
      'outputOrchestrator'
    )
    const localUrl = URL.createObjectURL(file)
    return { localUrl }
  }
}

export async function finalizeSession(sessionId: string): Promise<void> {
  await stateUploader.finalizeSession(sessionId)
}

export { stateUploader }
export type { ProcessVideoTaskParams } from '@renderer/utils/pipeline/fileProcessor'
